import {Color} from '@benchling/nucleus-ui';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {PLANNED, type UnitOperationStatusType} from './procedureRunOverviewUtils';

/**
 * Header background color for each unit operation status. Shared between the study procedure run
 * visualizer node and the procedure run design flowchart node so they stay visually identical.
 */
const UNIT_OPERATION_NODE_HEADER_COLORS: Record<UnitOperationStatusType, string> = {
  [ProcessTaskStatusType.COMPLETED]: Color.GREEN_100,
  [ProcessTaskStatusType.FAILED]: Color.GRAY_100,
  [ProcessTaskStatusType.IN_PROGRESS]: Color.BLUE_100,
  [ProcessTaskStatusType.SKIPPED]: Color.GRAY_100,
  [ProcessTaskStatusType.CANCELLED]: Color.GRAY_100,
  [ProcessTaskStatusType.PENDING]: Color.WHITE,
  [ProcessTaskStatusType.INVALID]: Color.GRAY_100,
  [PLANNED]: Color.WHITE,
};

/**
 * Returns the status icon and its color for a unit operation status, or undefined values when there
 * is no status. Uses {@link TASK_STATUS_ICONS} for task statuses and a calendar icon for `PLANNED`.
 */
export function getStatusIcon(status: UnitOperationStatusType | null): {
  icon: BenchlingIconName | undefined;
  iconColor: string | undefined;
} {
  if (status == null) {
    return {icon: undefined, iconColor: undefined};
  }

  if (status === PLANNED) {
    return {
      icon: 'calendar-days',
      iconColor: Color.GRAY_600,
    };
  }

  const taskStatusInfo = TASK_STATUS_ICONS[status];
  return {icon: taskStatusInfo?.icon, iconColor: taskStatusInfo?.style.color};
}

/**
 * Returns the header background color for a unit operation status, falling back to white when there
 * is no status.
 */
export function getStatusHeaderColor(status: UnitOperationStatusType | null): string {
  return status ? UNIT_OPERATION_NODE_HEADER_COLORS[status] : Color.WHITE;
}
